/* eslint-disable no-unused-vars */
import React from 'react';
import { FieldArray, ErrorMessage } from 'formik';
import { Grid, Box, Typography, IconButton, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { API_URL } from '../../../Config';
import { toast } from 'react-toastify';

export default function StepSevenUpdate({ values, setFieldValue, touched, errors }) {
   const [errorTexts, setErrorTexts] = React.useState({});
   const [previews, setPreviews] = React.useState({
      uploadedFile: [],
      signature: null,
   });
   const [userModifiedFields, setUserModifiedFields] = React.useState({
      signature: false,
      milestones: [],
   });

   // Refs for file inputs
   const fileInputRefs = React.useRef({});
   const signatureInputRef = React.useRef(null);

   // Redux State for Contract Prefilled images (normalize multiple possible shapes)
   const rawImageField = useSelector(
      (state) => state.contractState.contractUpdatePrefilledData?.image,
   );
   const directSignature = useSelector(
      (state) => state.contractState.contractUpdatePrefilledData?.signature,
   );

   const images = React.useMemo(() => {
      if (!rawImageField) return [];
      // case: array of filenames e.g. ['a.png','b.png']
      if (Array.isArray(rawImageField) && rawImageField.every((item) => typeof item === 'string')) {
         return rawImageField;
      }
      // case: array of objects, each maybe { image: [...] } or { image: 'file.png' }
      if (Array.isArray(rawImageField)) {
         const out = [];
         for (const item of rawImageField) {
            if (!item) continue;
            if (typeof item === 'string') out.push(item);
            else if (Array.isArray(item.image)) out.push(...item.image);
            else if (typeof item.image === 'string') out.push(item.image);
         }
         return out;
      }
      // case: single object with .image array or string
      if (typeof rawImageField === 'object') {
         if (Array.isArray(rawImageField.image)) {
            return rawImageField.image.slice();
         }
         if (typeof rawImageField.image === 'string') {
            return [rawImageField.image];
         }
      }
      return [];
   }, [rawImageField]);

   const signatureImage =
      directSignature ||
      images.find((img) => typeof img === 'string' && img.includes('signature')) ||
      null;
   const MilestoneImages = images.filter(
      (img) => typeof img === 'string' && !img.includes('signature'),
   );
   const MilestoneImageURL = MilestoneImages.map((image) => `${API_URL}MilestoneImage/${image}`);
   const signatureFilename = signatureImage?.includes('/')
      ? signatureImage.split('/').pop()
      : signatureImage;
   const SignatureImageURL = signatureImage ? `${API_URL}signature/${signatureFilename}` : null;

   // Instead of auto-filling uploadedFile (which interferes with removals),
   // maintain an array to mark prefilled (server) images as removed by the user.
   React.useEffect(() => {
      if (
         !Array.isArray(values.removedOldFiles) ||
         values.removedOldFiles.length < MilestoneImages.length
      ) {
         const arr = Array.from({ length: MilestoneImages.length }, (_, idx) =>
            values.removedOldFiles && values.removedOldFiles[idx] !== undefined
               ? values.removedOldFiles[idx]
               : false,
         );
         setFieldValue('removedOldFiles', arr);
      }
   }, [MilestoneImages, values.removedOldFiles, setFieldValue]);

   // Validate file type - only allow images
   const isValidImageFile = (file) => {
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return validImageTypes.includes(file.type);
   };

   const handleFileUpload = async (event, index) => {
      const file = event.currentTarget.files[0];

      if (!file) return;

      // Validate file type
      if (!isValidImageFile(file)) {
         toast.error('Please upload only image files (JPEG, PNG, GIF, WebP)');
         event.target.value = null; // Reset the file input
         return;
      }

      const updatedErrorTexts = { ...errorTexts };
      const updatedPreviews = { ...previews };

      try {
         updatedErrorTexts[index] = null;
         const updatedFiles = [...(values.uploadedFile || [])];
         updatedFiles[index] = file;
         setFieldValue('uploadedFile', updatedFiles);

         // If there was an old prefilled image at same index and it was marked removed, unmark so this slot shows the new file
         if (Array.isArray(values.removedOldFiles)) {
            const unremoved = [...values.removedOldFiles];
            unremoved[index] = false;
            setFieldValue('removedOldFiles', unremoved);
         }

         // Track that user has modified this milestone field
         const updatedMilestones = [...userModifiedFields.milestones];
         updatedMilestones[index] = true;
         setUserModifiedFields({
            ...userModifiedFields,
            milestones: updatedMilestones,
         });

         // Generate a preview
         const reader = new FileReader();
         reader.onload = (e) => {
            updatedPreviews.uploadedFile[index] = e.target.result;
            setPreviews({ ...updatedPreviews });
         };
         reader.readAsDataURL(file);
      } catch (error) {
         updatedErrorTexts[index] = 'Failed to load the image.';
         event.target.value = null; // Reset the file input
         setFieldValue(`uploadedFile[${index}]`, null);
         updatedPreviews.uploadedFile[index] = null;
         toast.error('Failed to process the image file');
      }

      setErrorTexts(updatedErrorTexts);
   };

   const handleSignatureUpload = (event) => {
      const file = event.currentTarget.files[0];

      if (!file) return;

      // Validate file type for signature
      if (!isValidImageFile(file)) {
         toast.error('Please upload only image files (JPEG, PNG, GIF, WebP) for signature');
         event.target.value = null;
         return;
      }

      const updatedPreviews = { ...previews };

      try {
         const reader = new FileReader();
         reader.onload = (e) => {
            updatedPreviews.signature = e.target.result;
            setPreviews({ ...updatedPreviews });
         };
         reader.readAsDataURL(file);
         setFieldValue('signature', file);
         setUserModifiedFields({
            ...userModifiedFields,
            signature: true,
         });
      } catch (error) {
         toast.error('Failed to process the signature image');
         event.target.value = null;
         updatedPreviews.signature = null;
         setPreviews({ ...updatedPreviews });
         setFieldValue('signature', null);
      }
   };

   // Helper to determine which image to show (new or old)
   const getMilestoneImageSrc = (file, index) => {
      // If a File object (new upload), create an object URL
      if (file && typeof file !== 'string' && file instanceof File) {
         return URL.createObjectURL(file);
      }
      // If it's already a string we assume it's a URL (either prefilled or provided)
      if (typeof file === 'string' && file) {
         return file;
      }
      return MilestoneImageURL[index] || '';
   };

   return (
      <>
         <div className="card stepCard">
            <Box className="card-body" sx={{ padding: '15px' }}>
               <Grid container spacing={2}>
                  <Grid item md={12} xs={12}>
                     <Box className="commonBox">
                        {/* Document Uploads */}
                        {/* <FieldArray
                           name="uploadedFile"
                           render={(arrayHelpers) => {
                              // Build a list of indices we want to render, combining uploadedFile and prefilled images
                              const totalSlots = Math.max(
                                 MilestoneImages.length,
                                 (values.uploadedFile && values.uploadedFile.length) || 0,
                              );
                              const indicesToShow = [];
                              for (let i = 0; i < totalSlots; i++) {
                                 indicesToShow.push(i);
                              }

                              return (
                                 <>
                                    {indicesToShow && indicesToShow.length > 0 ? (
                                       indicesToShow.map((idx) => {
                                          const file =
                                             values.uploadedFile &&
                                             values.uploadedFile[idx] !== undefined &&
                                             values.uploadedFile[idx] !== null
                                                ? values.uploadedFile[idx]
                                                : null;
                                          const hasPrefilledImage =
                                             MilestoneImageURL[idx] &&
                                             (!values.removedOldFiles ||
                                                !values.removedOldFiles[idx]);
                                          const showImage = file || hasPrefilledImage;

                                          return (
                                             <Grid
                                                container
                                                spacing={2}
                                                key={idx}
                                                alignItems="center"
                                             >
                                                <Grid item md={12} xs={12}>
                                                   <Box
                                                      sx={{
                                                         border: '1px solid grey',
                                                         borderRadius: '8px',
                                                         p: 2,
                                                         marginBottom: 2,
                                                         position: 'relative',
                                                      }}
                                                   >
                                                      <Typography
                                                         variant="h6"
                                                         gutterBottom
                                                         align="left"
                                                         sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                         }}
                                                      >
                                                         Upload Document {idx + 1}
                                                         <Chip
                                                            label="Optional"
                                                            color="primary"
                                                            size="small"
                                                            sx={{
                                                               ml: 1,
                                                               fontSize: '0.7rem',
                                                               height: '20px',
                                                            }}
                                                         />
                                                      </Typography>
                                                      {showImage && (
                                                         <IconButton
                                                            aria-label="delete"
                                                            onClick={() => {
                                                               const hasUploadedFile =
                                                                  values.uploadedFile &&
                                                                  values.uploadedFile[idx];
                                                               const hasPrefilled =
                                                                  !!MilestoneImageURL[idx];
                                                               if (hasUploadedFile) {
                                                                  // Clear the uploaded file from Formik array
                                                                  const updatedFiles = [
                                                                     ...(values.uploadedFile || []),
                                                                  ];
                                                                  updatedFiles[idx] = null;
                                                                  setFieldValue(
                                                                     'uploadedFile',
                                                                     updatedFiles,
                                                                  );
                                                                  const updatedPreviews = {
                                                                     ...previews,
                                                                  };
                                                                  if (
                                                                     Array.isArray(
                                                                        updatedPreviews.uploadedFile,
                                                                     )
                                                                  ) {
                                                                     updatedPreviews.uploadedFile[
                                                                        idx
                                                                     ] = null;
                                                                  }
                                                                  setPreviews(updatedPreviews);
                                                                  // Reset the file input
                                                                  if (fileInputRefs.current[idx]) {
                                                                     fileInputRefs.current[
                                                                        idx
                                                                     ].value = '';
                                                                  }
                                                               } else if (hasPrefilled) {
                                                                  // Mark prefilled (server) image as removed
                                                                  const updatedRemoved = [
                                                                     ...(values.removedOldFiles ||
                                                                        []),
                                                                  ];
                                                                  updatedRemoved[idx] = true;
                                                                  setFieldValue(
                                                                     'removedOldFiles',
                                                                     updatedRemoved,
                                                                  );
                                                                  // Reset the file input
                                                                  if (fileInputRefs.current[idx]) {
                                                                     fileInputRefs.current[
                                                                        idx
                                                                     ].value = '';
                                                                  }
                                                               }
                                                            }}
                                                            sx={{
                                                               position: 'absolute',
                                                               top: 8,
                                                               right: 8,
                                                               padding: 1,
                                                            }}
                                                         >
                                                            <DeleteIcon />
                                                         </IconButton>
                                                      )}
                                                      <input
                                                         ref={(el) =>
                                                            (fileInputRefs.current[idx] = el)
                                                         }
                                                         type="file"
                                                         accept="image/*"
                                                         onChange={(event) =>
                                                            handleFileUpload(event, idx)
                                                         }
                                                      />
                                                      <Typography
                                                         variant="caption"
                                                         color="text.secondary"
                                                         sx={{ display: 'block', mt: 1 }}
                                                      >
                                                         Only image files (JPEG, PNG, GIF, WebP) are
                                                         allowed
                                                      </Typography>

                                                      {showImage && (
                                                         <Box
                                                            sx={{
                                                               mt: 2,
                                                               border: '1px solid #ddd',
                                                               borderRadius: '8px',
                                                               overflow: 'hidden',
                                                            }}
                                                         >
                                                            <img
                                                               src={
                                                                  file
                                                                     ? file instanceof File
                                                                        ? URL.createObjectURL(file)
                                                                        : file
                                                                     : MilestoneImageURL[idx]
                                                               }
                                                               alt={`Preview ${idx + 1}`}
                                                               style={{
                                                                  width: '100%',
                                                                  maxWidth: '200px',
                                                                  marginTop: '10px',
                                                               }}
                                                            />
                                                         </Box>
                                                      )}

                                                      <ErrorMessage
                                                         name={`uploadedFile[${idx}]`}
                                                         component="div"
                                                         style={{
                                                            color: 'red',
                                                            marginTop: '8px',
                                                         }}
                                                      />
                                                      {errorTexts[idx] && (
                                                         <Typography variant="body2" color="error">
                                                            {errorTexts[idx]}
                                                         </Typography>
                                                      )}
                                                   </Box>
                                                </Grid>
                                             </Grid>
                                          );
                                       })
                                    ) : (
                                       <Grid container spacing={2}>
                                          <Typography
                                             variant="body2"
                                             color="textSecondary"
                                             sx={{ paddingLeft: 2 }}
                                          >
                                             No files uploaded yet.
                                          </Typography>
                                       </Grid>
                                    )}
                                    <Grid container spacing={2} justifyContent="flex-end">
                                       <IconButton
                                          className="addFieldBtn"
                                          aria-label="add"
                                          onClick={() => arrayHelpers.push(null)}
                                          sx={{ padding: 0 }}
                                       >
                                          <AddIcon />
                                       </IconButton>
                                    </Grid>
                                 </>
                              );
                           }}
                        /> */}

                        {/* Signature Upload */}
                        <Grid item xs={12} md={12} sx={{ mt: 3 }}>
                           <Box sx={{ border: '1px solid grey', borderRadius: '8px', p: 2 }}>
                              <Typography
                                 variant="h6"
                                 gutterBottom
                                 align="left"
                                 sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                 Upload Signature
                                 <Chip
                                    label="Optional"
                                    color="primary"
                                    size="small"
                                    sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                 />
                              </Typography>
                              <input
                                 ref={signatureInputRef}
                                 type="file"
                                 accept="image/*"
                                 onChange={handleSignatureUpload}
                              />
                              <Typography
                                 variant="caption"
                                 color="text.secondary"
                                 sx={{ display: 'block', mt: 1 }}
                              >
                                 Only image files (JPEG, PNG, GIF, WebP) are allowed
                              </Typography>

                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                 <Box sx={{ flex: 1 }}>
                                    {values.signature ? (
                                       <>
                                          <Box
                                             sx={{
                                                mt: 2,
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                             }}
                                          >
                                             <img
                                                src={URL.createObjectURL(values.signature)}
                                                alt={`Signature Preview`}
                                                style={{
                                                   width: '100%',
                                                   maxWidth: '200px',
                                                   marginTop: '10px',
                                                }}
                                             />
                                          </Box>
                                       </>
                                    ) : (
                                       <Box
                                          sx={{
                                             mt: 2,
                                             border: '1px solid #ddd',
                                             borderRadius: '8px',
                                             overflow: 'hidden',
                                          }}
                                       >
                                          {SignatureImageURL && !values.removedOldSignature ? (
                                             <img
                                                src={SignatureImageURL}
                                                alt={`Default Signature Preview`}
                                                style={{
                                                   width: '100%',
                                                   maxWidth: '200px',
                                                   marginTop: '10px',
                                                }}
                                             />
                                          ) : (
                                             <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ p: 2 }}
                                             >
                                                No signature available
                                             </Typography>
                                          )}
                                       </Box>
                                    )}
                                 </Box>
                                 {(values.signature ||
                                    (SignatureImageURL && !values.removedOldSignature)) && (
                                    <IconButton
                                       aria-label="delete signature"
                                       onClick={() => {
                                          setFieldValue('signature', null);
                                          setFieldValue('removedOldSignature', true);
                                          const updatedPreviews = { ...previews };
                                          updatedPreviews.signature = null;
                                          setPreviews(updatedPreviews);
                                          // Reset the file input
                                          if (signatureInputRef.current) {
                                             signatureInputRef.current.value = '';
                                          }
                                       }}
                                       sx={{ padding: 0, mt: 2 }}
                                    >
                                       <DeleteIcon />
                                    </IconButton>
                                 )}
                              </Box>
                              <ErrorMessage
                                 name="signature"
                                 component="div"
                                 style={{ color: 'red', marginTop: '8px' }}
                              />
                           </Box>
                        </Grid>
                     </Box>
                  </Grid>
               </Grid>
            </Box>
         </div>
      </>
   );
}
